<template>
  <div class="customizacao-page">
    <b-row>
      <!-- ── Coluna de configurações ──────────────────────────────── -->
      <b-col cols="12" md="7">
        <b-card>
          <template #header>
            <h4 class="mb-0">
              <feather-icon icon="SlidersIcon" class="mr-75" />
              Identidade Visual do Tenant
            </h4>
          </template>

          <!-- Nome da aplicação -->
          <b-form-group label="Nome da Aplicação" label-for="nome-app" class="mb-2">
            <b-form-input
              id="nome-app"
              v-model="form.nome"
              placeholder="Ex: Minha Empresa Comercial"
              maxlength="100"
            />
            <small class="text-muted">Nome exibido no topo da barra lateral e como título do sistema.</small>
          </b-form-group>

          <hr />

          <!-- Cor primária -->
          <b-form-group label="Cor Primária" label-for="cor-picker" class="mb-2">
            <div class="d-flex align-items-center">
              <input
                id="cor-picker"
                v-model="form.cor_primaria"
                type="color"
                class="color-input mr-1"
                @input="onColorPrimariaChange"
              />
              <b-form-input
                v-model="form.cor_primaria"
                placeholder="#1976D2"
                style="max-width: 120px;"
                @input="onColorPrimariaChange"
              />
              <b-button
                variant="outline-secondary"
                size="sm"
                class="ml-1"
                @click="resetarCorPrimaria"
              >
                Padrão
              </b-button>
            </div>
            <small class="text-muted">Afeta botões, links, sidebar e elementos de destaque em toda a aplicação.</small>
          </b-form-group>

          <!-- Cor secundária -->
          <b-form-group label="Cor Secundária" label-for="cor-sec-picker" class="mb-2">
            <div class="d-flex align-items-center">
              <input
                id="cor-sec-picker"
                v-model="form.cor_secundaria"
                type="color"
                class="color-input mr-1"
                @input="onColorSecundariaChange"
              />
              <b-form-input
                v-model="form.cor_secundaria"
                placeholder="#0ea5e9"
                style="max-width: 120px;"
                @input="onColorSecundariaChange"
              />
              <b-button
                variant="outline-secondary"
                size="sm"
                class="ml-1"
                @click="resetarCorSecundaria"
              >
                Padrão
              </b-button>
            </div>
            <small class="text-muted">Afeta badges, tags e elementos de ênfase secundária.</small>
          </b-form-group>

          <hr />

          <!-- Upload de logo -->
          <b-form-group label="Logo do Tenant" class="mb-2">
            <div
              class="upload-area"
              :class="{ 'drag-over': isDragging }"
              @dragenter.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @dragover.prevent
              @drop.prevent="onDrop"
              @click="$refs.fileInput.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                style="display: none"
                @change="onFileSelect"
              />
              <div v-if="!previewLogo" class="text-center py-2">
                <feather-icon icon="UploadIcon" size="32" class="text-muted mb-1" />
                <p class="mb-0 text-muted">
                  Arraste uma imagem ou <span class="text-primary" style="cursor:pointer">clique para selecionar</span>
                </p>
                <small class="text-muted">PNG, JPG, SVG, WebP — máx. 5 MB</small>
              </div>
              <div v-else class="text-center py-1">
                <img :src="previewLogo" alt="Preview logo" class="logo-preview-img" />
              </div>
            </div>

            <!-- Progresso do upload -->
            <b-progress
              v-if="uploadProgress > 0 && uploadProgress < 100"
              :value="uploadProgress"
              max="100"
              show-progress
              animated
              class="mt-1"
            />

            <!-- Ações do logo -->
            <div class="mt-1 d-flex align-items-center" v-if="previewLogo">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="removerLogo"
                :disabled="salvando"
              >
                <feather-icon icon="TrashIcon" class="mr-25" />
                Remover logo
              </b-button>
              <small v-if="logoSelecionado" class="ml-1 text-muted">
                Arquivo selecionado: {{ logoSelecionado.name }}
              </small>
            </div>
          </b-form-group>

          <hr />

          <!-- Botão salvar -->
          <div class="d-flex align-items-center">
            <b-button
              variant="primary"
              :disabled="salvando"
              @click="salvar"
            >
              <b-spinner v-if="salvando" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              {{ salvando ? 'Salvando...' : 'Salvar Customização' }}
            </b-button>
            <b-button
              variant="outline-secondary"
              class="ml-1"
              :disabled="salvando"
              @click="descartar"
            >
              Descartar
            </b-button>
          </div>
        </b-card>
      </b-col>

      <!-- ── Coluna de pré-visualização ──────────────────────────── -->
      <b-col cols="12" md="5">
        <b-card>
          <template #header>
            <h4 class="mb-0">
              <feather-icon icon="EyeIcon" class="mr-75" />
              Pré-visualização
            </h4>
          </template>

          <!-- Mock sidebar preview -->
          <div class="sidebar-preview" :style="{ '--preview-primary': form.cor_primaria }">
            <div class="sidebar-preview__header">
              <img
                v-if="previewLogo"
                :src="previewLogo"
                alt="Logo"
                class="sidebar-preview__logo"
              />
              <span v-else class="sidebar-preview__logo-placeholder">
                <feather-icon icon="ImageIcon" size="24" />
              </span>
              <span class="sidebar-preview__name">{{ form.nome || tenantNome }}</span>
            </div>
            <div class="sidebar-preview__nav">
              <div class="sidebar-preview__nav-item sidebar-preview__nav-item--active">
                <feather-icon icon="HomeIcon" size="16" class="mr-50" />
                Dashboard
              </div>
              <div class="sidebar-preview__nav-item">
                <feather-icon icon="ShoppingCartIcon" size="16" class="mr-50" />
                Pedidos
              </div>
              <div class="sidebar-preview__nav-item">
                <feather-icon icon="UsersIcon" size="16" class="mr-50" />
                Clientes
              </div>
            </div>
          </div>

          <!-- Amostra de botões e elementos -->
          <div class="mt-2">
            <p class="mb-50 text-muted" style="font-size: 12px;">Amostra de elementos com as cores selecionadas:</p>
            <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
              <b-button :style="{ 'background-color': form.cor_primaria, 'border-color': form.cor_primaria }" size="sm">
                Botão Primário
              </b-button>
              <b-button :style="{ 'background-color': form.cor_secundaria, 'border-color': form.cor_secundaria }" size="sm">
                Botão Secundário
              </b-button>
            </div>
            <div class="d-flex align-items-center mt-1" style="gap: 8px;">
              <span :style="{ color: form.cor_primaria, 'font-weight': 600 }">Link primário</span>
              <span :style="{ color: form.cor_secundaria, 'font-weight': 600 }">Link secundário</span>
            </div>
            <div class="d-flex align-items-center mt-1" style="gap: 6px;">
              <b-badge :style="{ 'background-color': form.cor_primaria }">Primário</b-badge>
              <b-badge :style="{ 'background-color': form.cor_secundaria }">Secundário</b-badge>
            </div>
          </div>
        </b-card>

        <!-- Info card -->
        <b-card class="border-left-info" style="border-left: 4px solid #17a2b8;">
          <h6 class="mb-50">
            <feather-icon icon="InfoIcon" class="mr-50 text-info" />
            Informações
          </h6>
          <ul class="pl-1 mb-0 text-muted" style="font-size: 13px;">
            <li>A cor primária é aplicada imediatamente após salvar.</li>
            <li>O logo aparece na barra lateral (sidebar).</li>
            <li>Formatos suportados: PNG, JPG, SVG, WebP.</li>
            <li>Tamanho recomendado: 200×60 px para logo completo.</li>
          </ul>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormGroup, BButton, BProgress,
  BSpinner, BBadge, BFormInput,
} from 'bootstrap-vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { applyTenantColor, applyTenantSecondaryColor } from '@/utils/tenantTheme';

export default {
  name: 'Customizacao',
  components: {
    BRow, BCol, BCard, BFormGroup, BButton, BProgress,
    BSpinner, BBadge, BFormInput, FeatherIcon,
  },
  data() {
    return {
      form: {
        nome:          '',
        cor_primaria:   '#1976D2',
        cor_secundaria: '#0ea5e9',
        logo_url: null,
      },
      previewLogo: null,
      logoSelecionado: null,
      uploadProgress: 0,
      isDragging: false,
      salvando: false,
      originalNome:          '',
      originalCorPrimaria:   '#1976D2',
      originalCorSecundaria: '#0ea5e9',
      originalLogoUrl: null,
    };
  },
  computed: {
    tenantNome() {
      return this.$store.getters['tenantStore/tenantNome'];
    },
    storeCorPrimaria() {
      return this.$store.getters['tenantStore/corPrimaria'];
    },
    storeCorSecundaria() {
      return this.$store.getters['tenantStore/corSecundaria'];
    },
    storeLogoUrl() {
      return this.$store.getters['tenantStore/logoUrl'];
    },
  },
  created() {
    this.carregarDados();
  },
  methods: {
    async carregarDados() {
      try {
        const CustomizationService = (await import('@/services/customization.service')).default;
        const data = await CustomizationService.get().catch(() => null);
        if (data) {
          this.form.nome             = data.tenant_nome    || '';
          this.form.cor_primaria     = data.cor_primaria   || '#1976D2';
          this.form.cor_secundaria   = data.cor_secundaria || '#0ea5e9';
          this.form.logo_url         = data.logo_url       || null;
          this.previewLogo           = data.logo_url       || null;
          this.originalNome          = data.tenant_nome    || '';
          this.originalCorPrimaria   = data.cor_primaria   || '#1976D2';
          this.originalCorSecundaria = data.cor_secundaria || '#0ea5e9';
          this.originalLogoUrl       = data.logo_url       || null;
          return;
        }
      } catch (_) {}
      // Fallback: usa store
      this.form.nome             = this.tenantNome         || '';
      this.form.cor_primaria     = this.storeCorPrimaria   || '#1976D2';
      this.form.cor_secundaria   = this.storeCorSecundaria || '#0ea5e9';
      this.form.logo_url         = this.storeLogoUrl       || null;
      this.previewLogo           = this.storeLogoUrl       || null;
      this.originalNome          = this.tenantNome         || '';
      this.originalCorPrimaria   = this.storeCorPrimaria   || '#1976D2';
      this.originalCorSecundaria = this.storeCorSecundaria || '#0ea5e9';
      this.originalLogoUrl       = this.storeLogoUrl       || null;
    },

    onColorPrimariaChange() {
      applyTenantColor(this.form.cor_primaria);
    },

    onColorSecundariaChange() {
      applyTenantSecondaryColor(this.form.cor_secundaria);
    },

    resetarCorPrimaria() {
      this.form.cor_primaria = '#1976D2';
      applyTenantColor('#1976D2');
    },

    resetarCorSecundaria() {
      this.form.cor_secundaria = '#0ea5e9';
      applyTenantSecondaryColor('#0ea5e9');
    },

    onFileSelect(event) {
      const file = event.target.files[0];
      if (file) this.processarArquivo(file);
    },

    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) this.processarArquivo(file);
    },

    processarArquivo(file) {
      const maxSize = 5 * 1024 * 1024;
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/gif'];

      if (!allowed.includes(file.type)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Formato inválido',
            text: 'Use PNG, JPG, SVG ou WebP.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      if (file.size > maxSize) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Arquivo muito grande',
            text: 'O tamanho máximo é 5 MB.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.logoSelecionado = file;
      const reader = new FileReader();
      reader.onload = (e) => { this.previewLogo = e.target.result; };
      reader.readAsDataURL(file);
    },

    removerLogo() {
      this.previewLogo     = null;
      this.logoSelecionado = null;
      this.form.logo_url   = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    descartar() {
      this.form.nome           = this.originalNome;
      this.form.cor_primaria   = this.originalCorPrimaria;
      this.form.cor_secundaria = this.originalCorSecundaria;
      this.form.logo_url       = this.originalLogoUrl;
      this.previewLogo         = this.originalLogoUrl;
      this.logoSelecionado     = null;
      this.uploadProgress      = 0;
      applyTenantColor(this.originalCorPrimaria);
      applyTenantSecondaryColor(this.originalCorSecundaria);
    },

    async salvar() {
      this.salvando = true;
      try {
        // 1. Upload do logo (se houver novo arquivo)
        if (this.logoSelecionado) {
          this.uploadProgress = 1;
          const result = await this.$store.dispatch('tenantStore/uploadLogo', {
            file: this.logoSelecionado,
            onProgress: (pct) => { this.uploadProgress = pct; },
          });
          this.form.logo_url = result.logo_url;
          this.previewLogo   = result.logo_url;
          this.logoSelecionado = null;
          this.uploadProgress = 0;
        }

        // 2. Atualiza logo removido (logo_url = null)
        else if (this.originalLogoUrl && !this.form.logo_url) {
          await this.$store.dispatch('tenantStore/salvarLogoUrl', null);
        }

        // 3. Salva nome, cor primária e secundária
        if (this.form.nome && this.form.nome !== this.originalNome) {
          await this.$store.dispatch('tenantStore/salvarNome', this.form.nome);
        }
        await this.$store.dispatch('tenantStore/salvarCorPrimaria',   this.form.cor_primaria);
        await this.$store.dispatch('tenantStore/salvarCorSecundaria', this.form.cor_secundaria);

        this.originalNome          = this.form.nome;
        this.originalCorPrimaria   = this.form.cor_primaria;
        this.originalCorSecundaria = this.form.cor_secundaria;
        this.originalLogoUrl       = this.form.logo_url;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Customização salva',
            text: 'Identidade visual atualizada com sucesso.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
      } catch (err) {
        console.error('[Customizacao] Erro ao salvar:', err);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar',
            text: err?.response?.data?.error || 'Verifique sua conexão e tente novamente.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        // Reverte cores em caso de erro
        applyTenantColor(this.originalCorPrimaria);
        applyTenantSecondaryColor(this.originalCorSecundaria);
        this.form.nome           = this.originalNome;
        this.form.cor_primaria   = this.originalCorPrimaria;
        this.form.cor_secundaria = this.originalCorSecundaria;
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.customizacao-page {
  .color-input {
    width: 48px;
    height: 38px;
    padding: 2px;
    border: 1px solid #d8d6de;
    border-radius: 6px;
    cursor: pointer;
    background: none;
  }

  .upload-area {
    border: 2px dashed #d8d6de;
    border-radius: 8px;
    padding: 20px;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
    min-height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: var(--primary, #1976D2);
      background-color: rgba(25, 118, 210, 0.04);
    }

    &.drag-over {
      border-color: var(--primary, #1976D2);
      background-color: rgba(25, 118, 210, 0.08);
    }
  }

  .logo-preview-img {
    max-height: 80px;
    max-width: 100%;
    object-fit: contain;
    border-radius: 4px;
  }
}

/* ── Sidebar Preview ─────────────────────────────────────────── */
.sidebar-preview {
  background: #283046;
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;

  &__header {
    background: #1e2a3b;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  &__logo {
    height: 36px;
    max-width: 120px;
    object-fit: contain;
  }

  &__logo-placeholder {
    width: 36px;
    height: 36px;
    background: rgba(255,255,255,0.1);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #adb5bd;
  }

  &__name {
    color: #fff;
    font-weight: 600;
    font-size: 14px;
  }

  &__nav {
    padding: 10px 0;
  }

  &__nav-item {
    padding: 9px 16px;
    color: #a6acb9;
    display: flex;
    align-items: center;
    cursor: default;
    border-left: 3px solid transparent;
    transition: all 0.15s;

    &--active {
      color: #fff;
      background: rgba(255,255,255,0.06);
      border-left-color: var(--preview-primary, #1976D2);
    }
  }
}
</style>
